import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ArrowLeft, Info } from 'lucide-react';

import ReleaseConfirmDialog from '@/components/release/ReleaseConfirmDialog';
import ReleaseItemList from '@/components/release/ReleaseItemList';
import ReleasePanelLayout from '@/components/release/ReleasePanelLayout';
import SectionTabs from '@/components/SectionTabs';
import { Button } from '@/components/ui/button';
import { DocumentContext } from '@/contexts/DocumentContext';
import { useControlById } from '@/hooks/useControlsAPI';
import { useDocumentById } from '@/hooks/useDocuments';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import PageLayout from '@/layouts/PageLayout';
import { apiService } from '@/lib/axios';

const controlTypeMap = {
  INITIAL_RELEASE: 'Initial Release',
  NEW_DOCUMENT: 'New Document',
  PERIODIC: 'Periodic Review',
  REVISION: 'Revision',
};

const ControlReleasePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotificationModal } = useNotificationModal();

  const { control: controlData } = useControlById(id);
  const { document: docData, load: loadDocument } = useDocumentById();
  const [template, setTemplate] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);

  // Load approved, unlinked documents
  useEffect(() => {
    setDocumentsLoading(true);
    apiService.documents
      .getAll({ status: 'APPROVED', unlinked: 'true', limit: 100 })
      .then((res) => {
        if (res.data.success) {
          setDocuments(res.data.data);
        }
      })
      .catch((err) => console.error('Failed to load documents:', err))
      .finally(() => setDocumentsLoading(false));
  }, []);

  // Load selected document details
  useEffect(() => {
    if (selectedDocumentId) {
      loadDocument(selectedDocumentId);
    }
  }, [selectedDocumentId, loadDocument]);

  useEffect(() => {
    if (docData?.templateId) {
      apiService.templates
        .getById(docData.templateId)
        .then((res) => {
          if (res.data.success) {
            setTemplate(res.data.data);
          }
        })
        .catch((err) => console.error('Failed to load template:', err));
    }
  }, [docData?.templateId]);

  const selectedDocumentListItem = documents.find(
    (d) => d.id === selectedDocumentId
  );

  const handleRelease = async () => {
    if (!selectedDocumentId || !controlData) return;
    setReleasing(true);
    try {
      const res = await apiService.controls.release(
        controlData.id,
        { linkedDocumentId: selectedDocumentId }
      );
      if (res.data.success) {
        showNotificationModal(
          'Release Successful',
          'The change control has been released and linked to the document.',
          'success'
        );
        navigate('/controls', { state: 'master' });
      }
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to release control';
      showNotificationModal('Release Failed', message, 'error');
    } finally {
      setReleasing(false);
      setConfirmDialogOpen(false);
    }
  };

  const renderDocumentItem = (item) => (
    <div>
      <div className="font-medium text-sm">{item.documentTitle}</div>
      <div className="text-xs text-muted-foreground mt-1">
        {item.serial || item.id}
      </div>
      <div className="text-xs text-muted-foreground">
        {item.creatorName || item.creator} &middot; Rev {item.revision}
      </div>
    </div>
  );

  return (
    <DocumentContext.Provider
      value={{
        form: docData,
        id: selectedDocumentId,
        isPreview: true,
        mode: 'static',
        setFormByKey: () => {},
      }}
    >
      <PageLayout
        title={`Release Change Control: ${controlData?.serial || id}`}
        toolbarHead={
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        }
        toolbarTail={
          <Button
            disabled={!selectedDocumentId}
            onClick={() => setConfirmDialogOpen(true)}
          >
            Release
          </Button>
        }
      >
        {controlData && (
          <div className="flex items-center gap-2 mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
            <Info className="h-4 w-4 text-blue-500 shrink-0" />
            <span>
              <span className="font-medium">Releasing:</span>{' '}
              {controlData.serial} — {controlTypeMap[controlData.typeChange] || controlData.typeChange}
            </span>
          </div>
        )}

        {documentsLoading ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Loading approved documents...
          </div>
        ) : documents.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            No approved documents available for linking
          </div>
        ) : (
          <ReleasePanelLayout
            leftPanel={
              <>
                <h3 className="font-semibold text-sm mb-3">
                  Approved Documents ({documents.length})
                </h3>
                <ReleaseItemList
                  items={documents}
                  selectedId={selectedDocumentId}
                  onSelect={setSelectedDocumentId}
                  renderItem={renderDocumentItem}
                  emptyMessage="No approved documents available for linking"
                />
              </>
            }
            rightPanel={
              docData && template ? (
                <SectionTabs
                  formData={docData}
                  mode="static"
                  template={template}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  Select a document to preview
                </div>
              )
            }
          />
        )}

        <ReleaseConfirmDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleRelease}
          title="Confirm Change Control Release"
          description="You are about to release this change control and link it to a document."
          details={[
            {
              label: 'Change Control',
              value: controlData?.serial || `CC ${id}`,
            },
            {
              label: 'Type of Change',
              value: controlTypeMap[controlData?.typeChange] || controlData?.typeChange || '',
            },
            {
              label: 'Document',
              value: selectedDocumentListItem?.documentTitle || '',
            },
            {
              label: 'Document Type',
              value: selectedDocumentListItem?.templateId || '',
            },
          ]}
          loading={releasing}
        />
      </PageLayout>
    </DocumentContext.Provider>
  );
};

export default ControlReleasePage;
